import { Mic, MicOff, Video, VideoOff, PhoneOff, Link2, ScreenShare, ScreenShareOff, Sparkles, Captions, CaptionsOff, ScrollText, Languages } from 'lucide-react';
import { CALL } from '@/constants/testIds';
import { useLanguage } from '../context/LanguageContext';

const ControlButton = ({ active, danger, highlight, onClick, testId, children, label, mobile, mobileLabel, desktopLabel }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    aria-label={label}
    title={label}
    className={
      mobile
        ? `flex h-14 w-14 flex-shrink-0 flex-col items-center justify-center gap-1 rounded-2xl transition-all duration-200 active:scale-95 focus:outline-none ${
            danger
              ? 'bg-red-500 text-white'
              : active
              ? 'bg-[#3B82F6] text-white'
              : 'bg-white/10 text-[#F8FAFC]'
          } ${highlight ? 'ring-2 ring-amber-400' : ''}`
        : `flex h-12 flex-col items-center justify-center gap-0.5 rounded-full px-3 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/60 ${
            danger
              ? 'bg-red-500 text-white hover:bg-red-600'
              : active
              ? 'bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90'
              : 'bg-white/10 text-[#F8FAFC] hover:bg-white/20'
          } ${highlight ? 'ring-2 ring-amber-400' : 'w-12'}`
    }
  >
    {children}
    {mobile && mobileLabel && <span className="text-[10px] leading-none">{mobileLabel}</span>}
    {!mobile && desktopLabel && <span className="whitespace-nowrap text-[10px] leading-none">{desktopLabel}</span>}
  </button>
);

export const CallControls = ({
  muted,
  cameraOff,
  screenSharing,
  bgBlurOn,
  bgBlurSupported = true,
  captionsOn,
  captionPanelOpen,
  captionsFeatureEnabled,
  isHost,
  onToggleMic,
  onToggleCamera,
  onToggleScreenShare,
  onToggleBlur,
  onToggleCaptions,
  onToggleCaptionPanel,
  onToggleCaptionsFeature,
  onLeave,
  onCopyLink,
  isMobile = false,
}) => {
  const { t } = useLanguage();
  if (isMobile) {
    return (
      <div
        data-testid={CALL.controlsBar}
        className="fixed inset-x-0 bottom-0 z-30 overflow-x-auto border-t border-white/10 bg-[#18181b]/95 px-2 pt-3 backdrop-blur-xl [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto flex w-max items-center gap-1">
          <ControlButton mobile mobileLabel={muted ? t('call.unmute') : t('call.mute')} testId={CALL.micBtn} active={muted} onClick={onToggleMic} label={t('call.ariaToggleMic')}>
            {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </ControlButton>
          <ControlButton mobile mobileLabel={cameraOff ? t('call.startVideo') : t('call.stopVideo')} testId={CALL.cameraBtn} active={cameraOff} onClick={onToggleCamera} label={t('call.ariaToggleCamera')}>
            {cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
          </ControlButton>
          <ControlButton
            mobile
            mobileLabel={t('call.share')}
            testId={CALL.screenShareBtn}
            active={screenSharing}
            onClick={onToggleScreenShare}
            label={t('call.ariaToggleShare')}
          >
            {screenSharing ? <ScreenShareOff className="h-5 w-5" /> : <ScreenShare className="h-5 w-5" />}
          </ControlButton>
          {bgBlurSupported && (
            <ControlButton mobile mobileLabel={t('call.blur')} testId={CALL.blurBtn} active={bgBlurOn} onClick={onToggleBlur} label={t('call.ariaToggleBlur')}>
              <Sparkles className="h-5 w-5" />
            </ControlButton>
          )}
          {isHost && (
            <ControlButton
              mobile
              mobileLabel={t('call.captionsFeature')}
              testId={CALL.captionsFeatureBtn}
              active={captionsFeatureEnabled}
              highlight={!captionsFeatureEnabled}
              onClick={onToggleCaptionsFeature}
              label={t('call.ariaToggleCaptionsFeature')}
            >
              <Languages className="h-5 w-5" />
            </ControlButton>
          )}
          {captionsFeatureEnabled && (
            <>
              <ControlButton mobile mobileLabel={t('call.captions')} testId={CALL.captionsBtn} active={captionsOn} onClick={onToggleCaptions} label={t('call.ariaToggleCaptions')}>
                {captionsOn ? <Captions className="h-5 w-5" /> : <CaptionsOff className="h-5 w-5" />}
              </ControlButton>
              <ControlButton mobile mobileLabel={t('call.captionPanel')} testId={CALL.captionPanelBtn} active={captionPanelOpen} onClick={onToggleCaptionPanel} label={t('call.ariaToggleCaptionPanel')}>
                <ScrollText className="h-5 w-5" />
              </ControlButton>
            </>
          )}
          <ControlButton mobile mobileLabel={t('call.invite')} testId={CALL.copyLinkBtn} onClick={onCopyLink} label={t('call.ariaCopyLink')}>
            <Link2 className="h-5 w-5" />
          </ControlButton>
          <ControlButton mobile mobileLabel={isHost ? t('call.endMeeting') : t('call.leave')} danger testId={isHost ? CALL.endMeetingBtn : CALL.leaveBtn} onClick={onLeave} label={t('call.ariaLeave')}>
            <PhoneOff className="h-5 w-5" />
          </ControlButton>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={CALL.controlsBar}
      className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-4 rounded-full border border-white/10 bg-[#18181b]/70 px-6 py-4 backdrop-blur-xl"
    >
      <ControlButton testId={CALL.micBtn} active={muted} onClick={onToggleMic} label={t('call.ariaToggleMic')}>
        {muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
      </ControlButton>
      <ControlButton testId={CALL.cameraBtn} active={cameraOff} onClick={onToggleCamera} label={t('call.ariaToggleCamera')}>
        {cameraOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
      </ControlButton>
      <ControlButton
        testId={CALL.screenShareBtn}
        active={screenSharing}
        onClick={onToggleScreenShare}
        label={t('call.ariaToggleShare')}
      >
        {screenSharing ? <ScreenShareOff className="h-5 w-5" /> : <ScreenShare className="h-5 w-5" />}
      </ControlButton>
      {bgBlurSupported && (
        <ControlButton testId={CALL.blurBtn} active={bgBlurOn} onClick={onToggleBlur} label={t('call.ariaToggleBlur')}>
          <Sparkles className="h-5 w-5" />
        </ControlButton>
      )}
      {isHost && (
        <ControlButton
          testId={CALL.captionsFeatureBtn}
          active={captionsFeatureEnabled}
          highlight={!captionsFeatureEnabled}
          desktopLabel={t('call.captionsFeature')}
          onClick={onToggleCaptionsFeature}
          label={t('call.ariaToggleCaptionsFeature')}
        >
          <Languages className="h-5 w-5" />
        </ControlButton>
      )}
      {captionsFeatureEnabled && (
        <>
          <ControlButton testId={CALL.captionsBtn} active={captionsOn} onClick={onToggleCaptions} label={t('call.ariaToggleCaptions')}>
            {captionsOn ? <Captions className="h-5 w-5" /> : <CaptionsOff className="h-5 w-5" />}
          </ControlButton>
          <ControlButton testId={CALL.captionPanelBtn} active={captionPanelOpen} onClick={onToggleCaptionPanel} label={t('call.ariaToggleCaptionPanel')}>
            <ScrollText className="h-5 w-5" />
          </ControlButton>
        </>
      )}
      <ControlButton testId={CALL.copyLinkBtn} onClick={onCopyLink} label={t('call.ariaCopyLink')}>
        <Link2 className="h-5 w-5" />
      </ControlButton>
      {isHost ? (
        <button
          type="button"
          data-testid={CALL.endMeetingBtn}
          onClick={onLeave}
          aria-label={t('call.endMeeting')}
          title={t('call.endMeeting')}
          className="flex h-12 items-center gap-2 rounded-full bg-red-500 px-5 text-white transition-all duration-200 hover:scale-105 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300"
        >
          <PhoneOff className="h-5 w-5" />
          <span className="whitespace-nowrap text-sm font-medium">{t('call.endMeeting')}</span>
        </button>
      ) : (
        <ControlButton testId={CALL.leaveBtn} danger onClick={onLeave} label={t('call.ariaLeave')}>
          <PhoneOff className="h-5 w-5" />
        </ControlButton>
      )}
    </div>
  );
};
